import { create } from 'zustand';

/**
 * Top-level project tabs (the tab host in the outer window).
 *
 * `active === null` is the Home tab (project list). Every other tab is a
 * project running in its own iframe. The tab list and the active tab persist
 * across reloads; unsaved state is live-only and reported by each frame.
 */

export interface ProjectTab {
  readonly projectId: number;
  readonly name: string;
}

export interface TabsSnapshot {
  readonly tabs: readonly ProjectTab[];
  /** Project id of the visible tab, or null for Home. */
  readonly active: number | null;
}

export const EMPTY_TABS: TabsSnapshot = { tabs: [], active: null };

/* ---------------- pure transitions ---------------- */

/** Open a project, or focus its tab when it is already open. */
export function openTab(state: TabsSnapshot, projectId: number, name: string): TabsSnapshot {
  const existing = state.tabs.find((t) => t.projectId === projectId);
  if (existing) return { tabs: state.tabs, active: projectId };
  return { tabs: [...state.tabs, { projectId, name }], active: projectId };
}

/** Focus a tab (null = Home). Unknown ids are ignored. */
export function focusTab(state: TabsSnapshot, projectId: number | null): TabsSnapshot {
  if (projectId !== null && !state.tabs.some((t) => t.projectId === projectId)) return state;
  if (state.active === projectId) return state;
  return { tabs: state.tabs, active: projectId };
}

/**
 * Close a tab. Closing the visible tab focuses its right-hand neighbour, else
 * the left one, else Home -- the way editors and browsers behave.
 */
export function closeTab(state: TabsSnapshot, projectId: number): TabsSnapshot {
  const index = state.tabs.findIndex((t) => t.projectId === projectId);
  if (index < 0) return state;
  const tabs = state.tabs.filter((t) => t.projectId !== projectId);
  if (state.active !== projectId) return { tabs, active: state.active };
  const next = tabs[index] ?? tabs[index - 1] ?? null;
  return { tabs, active: next ? next.projectId : null };
}

/** Move a tab to `toIndex` (clamped). */
export function moveTab(state: TabsSnapshot, projectId: number, toIndex: number): TabsSnapshot {
  const from = state.tabs.findIndex((t) => t.projectId === projectId);
  if (from < 0) return state;
  const to = Math.max(0, Math.min(state.tabs.length - 1, Math.trunc(toIndex)));
  if (from === to) return state;
  const tabs = [...state.tabs];
  const [tab] = tabs.splice(from, 1);
  tabs.splice(to, 0, tab!);
  return { tabs, active: state.active };
}

/** Step through Home + every project tab, wrapping at both ends. */
export function cycleTab(state: TabsSnapshot, delta: number): TabsSnapshot {
  const order: Array<number | null> = [null, ...state.tabs.map((t) => t.projectId)];
  if (order.length < 2) return state;
  const current = Math.max(0, order.indexOf(state.active));
  const next = (((current + delta) % order.length) + order.length) % order.length;
  return focusTab(state, order[next] ?? null);
}

export function renameTab(state: TabsSnapshot, projectId: number, name: string): TabsSnapshot {
  if (!name) return state;
  let changed = false;
  const tabs = state.tabs.map((t) => {
    if (t.projectId !== projectId || t.name === name) return t;
    changed = true;
    return { ...t, name };
  });
  return changed ? { tabs, active: state.active } : state;
}

/** Drop tabs whose project no longer exists and refresh names from the server list. */
export function reconcileTabs(
  state: TabsSnapshot,
  projects: ReadonlyArray<{ id: number; name: string }>,
): TabsSnapshot {
  const byId = new Map(projects.map((p) => [p.id, p.name]));
  let next = state;
  for (const tab of state.tabs) {
    const name = byId.get(tab.projectId);
    next =
      name === undefined ? closeTab(next, tab.projectId) : renameTab(next, tab.projectId, name);
  }
  return next;
}

/* ---------------- persistence ---------------- */

export const TABS_STORAGE_KEY = 'ai-studio.project-tabs';

/** Parse a persisted snapshot, tolerating anything malformed. */
export function parseTabs(raw: string | null | undefined): TabsSnapshot {
  if (!raw) return EMPTY_TABS;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return EMPTY_TABS;
  }
  if (!data || typeof data !== 'object') return EMPTY_TABS;
  const record = data as { tabs?: unknown; active?: unknown };
  const tabs: ProjectTab[] = [];
  const seen = new Set<number>();
  if (Array.isArray(record.tabs)) {
    for (const item of record.tabs as unknown[]) {
      if (!item || typeof item !== 'object') continue;
      const { projectId, name } = item as { projectId?: unknown; name?: unknown };
      if (typeof projectId !== 'number' || !Number.isSafeInteger(projectId) || projectId <= 0)
        continue;
      if (seen.has(projectId)) continue;
      seen.add(projectId);
      tabs.push({
        projectId,
        name: typeof name === 'string' ? name.slice(0, 200) : `Project ${projectId}`,
      });
    }
  }
  const active =
    typeof record.active === 'number' && seen.has(record.active) ? record.active : null;
  return { tabs, active };
}

export function serializeTabs(state: TabsSnapshot): string {
  return JSON.stringify({
    tabs: state.tabs.map((t) => ({ projectId: t.projectId, name: t.name })),
    active: state.active,
  });
}

function loadPersisted(): TabsSnapshot {
  try {
    return typeof localStorage === 'undefined'
      ? EMPTY_TABS
      : parseTabs(localStorage.getItem(TABS_STORAGE_KEY));
  } catch {
    return EMPTY_TABS;
  }
}

function persist(state: TabsSnapshot): void {
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem(TABS_STORAGE_KEY, serializeTabs(state));
  } catch {
    // Private mode or blocked storage: tabs just won't survive a reload.
  }
}

/* ---------------- store ---------------- */

interface ProjectTabsState extends TabsSnapshot {
  /** Unsaved work per project, as last reported by its frame. */
  dirty: Readonly<Record<number, boolean>>;
  /** Items waiting on the user per project (generation approvals and reviews), as reported by its frame. */
  attention: Readonly<Record<number, number>>;

  open: (projectId: number, name: string) => void;
  focus: (projectId: number | null) => void;
  /** Remove a tab outright. Callers confirm unsaved work first. */
  remove: (projectId: number) => void;
  move: (projectId: number, toIndex: number) => void;
  cycle: (delta: number) => void;
  reconcile: (projects: ReadonlyArray<{ id: number; name: string }>) => void;
  report: (projectId: number, name: string, dirty: boolean) => void;
  anyDirty: () => boolean;
  setAttention: (projectId: number, count: number) => void;
}

export const useProjectTabs = create<ProjectTabsState>((set, get) => {
  /** Apply a pure transition and persist when it changed anything. */
  const apply = (fn: (state: TabsSnapshot) => TabsSnapshot) => {
    const current = get();
    const next = fn(current);
    if (next.tabs === current.tabs && next.active === current.active) return;
    const dirty = { ...current.dirty };
    for (const key of Object.keys(dirty)) {
      if (!next.tabs.some((t) => t.projectId === Number(key))) delete dirty[Number(key)];
    }
    const attention = { ...current.attention };
    for (const key of Object.keys(attention)) {
      if (!next.tabs.some((t) => t.projectId === Number(key))) delete attention[Number(key)];
    }
    set({ tabs: next.tabs, active: next.active, dirty, attention });
    persist(next);
  };

  return {
    ...loadPersisted(),
    dirty: {},
    attention: {},

    open: (projectId, name) => apply((s) => openTab(s, projectId, name)),
    focus: (projectId) => apply((s) => focusTab(s, projectId)),
    remove: (projectId) => apply((s) => closeTab(s, projectId)),
    move: (projectId, toIndex) => apply((s) => moveTab(s, projectId, toIndex)),
    cycle: (delta) => apply((s) => cycleTab(s, delta)),
    reconcile: (projects) => apply((s) => reconcileTabs(s, projects)),
    report: (projectId, name, dirty) => {
      apply((s) => renameTab(s, projectId, name));
      if (!get().tabs.some((t) => t.projectId === projectId)) return;
      if ((get().dirty[projectId] ?? false) !== dirty) {
        set((s) => ({ dirty: { ...s.dirty, [projectId]: dirty } }));
      }
    },
    anyDirty: () => Object.values(get().dirty).some(Boolean),
    setAttention: (projectId, count) => {
      if (!get().tabs.some((t) => t.projectId === projectId)) return;
      if ((get().attention[projectId] ?? 0) === count) return;
      set((s) => ({ attention: { ...s.attention, [projectId]: count } }));
    },
  };
});
